import {createContext, FC, PropsWithChildren, useContext, useEffect} from "react";
import {
  UserProfileFragment,
  useMyProfileQuery,
  useLogoutMutation
} from "../../generated/graphql";
import { useRouter } from "next/router";
import {
  clearAuthenticatedSession,
  getLoginUrlAfterAuthFailure,
  isPublicPath,
  markAuthenticatedSession,
} from "@/lib/redirect";

export type ProfileContextValues = {
  profile?: UserProfileFragment | null | undefined;
  loading: boolean;
  impersonating?: boolean;
  logout: () => Promise<any>;
  refetch: () => Promise<any>;
}

export const ProfileContext = createContext<ProfileContextValues>({
  loading: true, logout: async () => {
  }, refetch: async () => {}
})

export const ProfileProvider: FC<PropsWithChildren> = ({children}) => {
  const router = useRouter();
  const query = useMyProfileQuery({
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });
  const profile = query.data?.myProfile?.id ? query.data.myProfile : null
  const [logout] = useLogoutMutation()

  // Redirect to login when user gets logged out (profile becomes null after loading)
  useEffect(() => {
    if (query.loading || typeof window === 'undefined') return;

    if (profile) {
      markAuthenticatedSession();
      return;
    }

    if (!profile) {
      // Only redirect if we're not already on login page
      if (
        !window.location.pathname.startsWith('/auth/') &&
        !isPublicPath(window.location.pathname)
      ) {
        const currentPath = window.location.pathname + (window.location.search || "");
        router.replace(getLoginUrlAfterAuthFailure(currentPath));
      } else if (window.location.pathname.startsWith('/auth/')) {
        // Keep the marker through navigation so simultaneous auth-failure
        // handlers agree, then clear it once the login page is reached.
        clearAuthenticatedSession();
      }
    }
  }, [query.loading, profile, router]);

  // Keep auth state fresh across navigation/focus so we don't cling to a cached profile forever.
  useEffect(() => {
    const onRouteDone = () => {
      // Best-effort: if this fails, the Apollo errorLink will still redirect on 401s.
      query.refetch?.().catch(() => undefined);
    };
    router.events.on("routeChangeComplete", onRouteDone);

    const onFocus = () => query.refetch?.().catch(() => undefined);
    if (typeof window !== "undefined") {
      window.addEventListener("focus", onFocus);
      document.addEventListener("visibilitychange", onFocus);
    }

    return () => {
      router.events.off("routeChangeComplete", onRouteDone);
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", onFocus);
        document.removeEventListener("visibilitychange", onFocus);
      }
    };
  }, [router.events, query.refetch]);

  return <ProfileContext.Provider value={{
    profile,
    loading: query.loading,
    logout,
    refetch: query.refetch,
  }}>
    {children}
  </ProfileContext.Provider>
}

export const useProfile = () => useContext(ProfileContext)
